import { IDefaultPermission, IPermission, IUpdatePermission, IUserPermission, Module, PermissionAccessRole, Scope } from "@interfaces/fc-user/permissions";
import axiosApiInstance from "@services/custom-axios-instance";
import { generateUrl } from "@services/generateUrl/generateUrl";
import axios from "axios";

/**
 * Helper function that will attach the organizationID as a query parameter
 * @param url 
 * @param organizationId 
 * @returns 
 */
function getOrganizationParams(url: string, organizationId?: number): string
{
    const params = new URLSearchParams();
    if(organizationId)
        params.append("organizationId", organizationId.toString());

    const strParams = params.toString();
    if(strParams)
        url += `?${strParams}`;
    return url;
}

/**
 * Get organization user permissions
 * @param username Target user
 * @param organizationId Target organization
 * @param platforms Platforms to search
 */
export async function getPermissions(username: string, organizationId?: number, platforms?: Scope[])
{
    let url = `${generateUrl()}/fcUser/user/permission/${username}`;

    const params = new URLSearchParams();
    if(platforms && platforms.length > 0)
        platforms.forEach((scope) => params.append("scope", scope));
    if(organizationId)
        params.append("organizationId", organizationId.toString());

    const strParams = params.toString();
    if(strParams)
        url += `?${strParams}`;
    const response = await axiosApiInstance.get(url);
    return response.data as IUserPermission;
}

export async function checkUserPermission(username: string, scope: Scope, permission: Module, organizationId?: number)
{
    try
    {
        let url = `${generateUrl()}/fcUser/user/permission/${username}/${scope}/${permission}`;
        url = getOrganizationParams(url, organizationId);
        
        const response = await axiosApiInstance.get(url);
        return response.data as IPermission;
    }
    catch(error)
    {
        if(axios.isAxiosError(error))
        {
            if(error.response?.status === 404)
            {
                return undefined
            }
        }
        console.error("There was an error on the request", error);
        throw new Error("Unhandled error on getting user's permission");
    }
}

/**
 * Add a permission to an existing user
 */
export async function addPermissionToUser(username: string, scope: Scope, permission: IPermission, organizationId?: number)
{
    let url = `${generateUrl()}/fcUser/user/permission/${username}`;
    url = getOrganizationParams(url, organizationId);
    const data = {
        scope: scope,
        permission: permission
    }
    const response = await axiosApiInstance.put(url, data);
    return response.data as boolean;
}

/**
 * Add multiple permissions to an existing user
 */
export async function addMultiplePermissionsToUser(username: string, scope: Scope, permissions: IPermission[], organizationId?: number)
{
    let url = `${generateUrl()}/fcUser/user/permissions/${username}`;
    url = getOrganizationParams(url, organizationId);
    const data = permissions.map(p => {
        return {
            permission: p,
            scope: scope
        }
    })
    const response = await axiosApiInstance.patch(url, data);
    return response.data as boolean;
}
export async function editPermissionsToUser(username: string, data:IUpdatePermission, organizationId?: number)
{
    let url = `${generateUrl()}/fcUser/user/permissions/${username}`;
    url = getOrganizationParams(url, organizationId);
    const response = await axiosApiInstance.put(url, data);
    return response.data as boolean;
}

/**
 * Saves the DB object (upsert).
 * 
 * Use on new savings
 * 
 * @param username: Username that will be used to save the data
 * @param permissions: Data to save
 * @param organizationId Could be used as a filter
 */
export async function savePermissions(permissions: IUserPermission)
{
    let url = `${generateUrl()}/fcUser/user/permissions/${permissions.user.username || permissions.user.email}`;
    url = getOrganizationParams(url, permissions.organizationId);
    const response = await axiosApiInstance.post(url, permissions);
    return response.data as boolean;
}

export async function getDefaultPermissions(role: PermissionAccessRole)
{
    const url = `${generateUrl()}/fcUser/user/permission/default/${role}`;
    const response = await axiosApiInstance.get(url);
    return response.data as IDefaultPermission[];
}

export async function getDefaultPermissionsScope(role: PermissionAccessRole, scope:Scope)
{
    const url = `${generateUrl()}/fcUser/user/permission/default/${role}/${scope}`;
    const response = await axiosApiInstance.get(url);
    return response.data as IDefaultPermission;
}

export async function getPermissionsModuleByScope(platform: Scope, appOrigin?:string)
{
    let url = `${generateUrl()}/fcUser/settings/permissions/modules/${platform}`;
    if(appOrigin && appOrigin === "mpc") //TODO: Change to string array of some configuration
        url += `?appOrigin=${appOrigin}`;
    const response = await axiosApiInstance.get(url);
    return response.data as Module[];
}